import { Injectable, signal, computed, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BeforeInstallPromptEvent, PwaPlatform } from '../models/pwa.model';

@Injectable({
  providedIn: 'root'
})
export class PwaInstallService {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  private readonly STORAGE_DISMISSED_KEY = 'eclesys_pwa_install_dismissed_at';
  private readonly DISMISS_DURATION_MS = 15 * 24 * 60 * 60 * 1000; // 15 dias em milissegundos

  // Signals de Estado
  readonly deferredPrompt = signal<BeforeInstallPromptEvent | null>(null);
  readonly isStandalone = signal<boolean>(false);
  readonly platform = signal<PwaPlatform>('other');
  readonly showInstallPrompt = signal<boolean>(false);
  readonly showGuideModal = signal<boolean>(false);

  // Computado: O app é instalável se não for standalone e for iOS OU possuir evento deferred
  readonly isInstallable = computed(() => {
    if (this.isStandalone()) return false;
    return this.platform() === 'ios' || !!this.deferredPrompt();
  });

  constructor() {
    if (this.isBrowser) {
      this.initPwaEnvironment();
    }
  }

  private initPwaEnvironment(): void {
    // 1. Detectar modo de exibição Standalone
    const isStandaloneMode = 
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true ||
      document.referrer.includes('android-app://');

    this.isStandalone.set(isStandaloneMode);

    // Se já estiver instalado em standalone, nenhuma ação adicional de sugestão é necessária
    if (isStandaloneMode) {
      return;
    }

    // 2. Detectar Plataforma
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent) || 
      (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    const isAndroidDevice = /android/.test(userAgent);

    if (isIOSDevice) {
      this.platform.set('ios');
    } else if (isAndroidDevice) {
      this.platform.set('android');
    } else {
      this.platform.set('desktop');
    }

    // 3. Capturar evento 'beforeinstallprompt' (Android/Chromium/Desktop)
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredPrompt.set(e as BeforeInstallPromptEvent);
      this.evaluateAutoPromptDisplay();
    });

    // 4. Capturar evento 'appinstalled' para limpeza imediata de prompts
    window.addEventListener('appinstalled', () => {
      this.deferredPrompt.set(null);
      this.isStandalone.set(true);
      this.showInstallPrompt.set(false);
      this.showGuideModal.set(false);
    });

    // 5. Se for iOS, avaliar exibição após delay de carregamento
    if (isIOSDevice) {
      this.evaluateAutoPromptDisplay();
    }
  }

  /**
   * Avalia a regra de silenciamento de 15 dias e aciona o prompt automático com delay suave
   */
  private evaluateAutoPromptDisplay(): void {
    if (this.isStandalone()) return;

    const dismissedAtStr = localStorage.getItem(this.STORAGE_DISMISSED_KEY);
    if (dismissedAtStr) {
      const dismissedAt = parseInt(dismissedAtStr, 10);
      const now = Date.now();
      if (!isNaN(dismissedAt) && now - dismissedAt < this.DISMISS_DURATION_MS) {
        // Silenciado por estar dentro da janela de 15 dias
        return;
      }
    }

    // Exibe o prompt com delay de 2.5s após inicialização para não competir com LCP
    setTimeout(() => {
      if (!this.isStandalone() && this.isInstallable()) {
        this.showInstallPrompt.set(true);
      }
    }, 2500);
  }

  /**
   * Dispara o fluxo de instalação de acordo com a plataforma
   */
  async promptInstall(): Promise<void> {
    if (this.platform() === 'ios') {
      this.showInstallPrompt.set(false);
      this.showGuideModal.set(true);
      return;
    }

    const promptEvent = this.deferredPrompt();
    if (promptEvent) {
      this.showInstallPrompt.set(false);
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      if (choice.outcome === 'accepted') {
        this.deferredPrompt.set(null);
      } else {
        this.dismissPrompt();
      }
    } else {
      // Fallback para guia genérico caso acionado manualmente
      this.showGuideModal.set(true);
    }
  }

  /**
   * Fecha o prompt contextual e grava o timestamp no localStorage para silenciar por 15 dias
   */
  dismissPrompt(): void {
    this.showInstallPrompt.set(false);
    if (this.isBrowser) {
      localStorage.setItem(this.STORAGE_DISMISSED_KEY, Date.now().toString());
    }
  }

  openGuideModal(): void {
    this.showGuideModal.set(true);
  }

  closeGuideModal(): void {
    this.showGuideModal.set(false);
  }
}
